import { useRef } from 'react';
import ArtistRow from './ArtistRow';
import { groupLineupByDay, rowSizeForArtist, splitByRole } from '../../utils/lineupFormat';

/**
 * Read-only lineup for the viewing surface — same role grouping as the vote list,
 * but rows open the artist detail modal instead of toggling a pick inline.
 *
 * votersByArtist + totalVoters (optional): a group is in view, so each row carries who in it wants
 * that artist ("2/3"), as the group Lineup page does, while keeping this list's headliner/support sizes
 * (the Sets page, 2026-09-23).
 */
export default function LineupViewList({
  lineup = [],
  onPress,
  filterDays = null,
  singleDay = null,
  listRef,
  votersByArtist = null,
  totalVoters = null,
  onQuickToggle,
  isPicked,
}) {
  const internalRef = useRef(null);
  const scrollRef = listRef || internalRef;

  const visible = lineup.filter((row) => {
    if (singleDay) return row.performance_day === singleDay;
    if (filterDays) return !row.performance_day || filterDays.includes(row.performance_day);
    return true;
  });

  if (visible.length === 0) {
    return <p className="py-6 text-center text-sm text-ink/60">No lineup announced yet.</p>;
  }

  const days = singleDay ? [{ day: singleDay, rows: visible }] : groupLineupByDay(visible);

  return (
    <div ref={scrollRef}>
      {days.map(({ day, rows }) => {
        const blocks = splitByRole(rows);

        return (
          <section key={day ?? 'no-day'}>
            {blocks.map((block) => {
              const labelClass =
                block.key === 'headliner'
                  ? 'role-block-label role-block-label--headliner'
                  : 'role-block-label role-block-label--support';
              // Null label and no set times means this block gets no header at
              // all: it is the acts below the headliners, and "Support" is
              // billing language rather than something to tell an attendee.
              const heading = block.timeRange ?? block.label;

              return (
                <div key={`${day ?? 'no-day'}:${block.key}`}>
                  {heading ? <h4 className={labelClass}>{heading}</h4> : null}
                  <div>
                    {block.rows.map((row) => (
                      <ArtistRow
                        key={`${row.artist_id}|${row.performance_day ?? ''}`}
                        artist={row}
                        size={rowSizeForArtist(row)}
                        layout="view"
                        voters={votersByArtist?.[row.artist_id]}
                        totalVoters={votersByArtist ? totalVoters : undefined}
                        onPress={onPress}
                        onQuickToggle={onQuickToggle}
                        picked={isPicked ? isPicked(row.artist_id, row.performance_day) : false}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}
